import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Euro, TrendingUp, Building2, Users, Zap, ArrowUpRight, ArrowDownRight,
  ChevronDown, ChevronUp, Percent, Wallet, BarChart3, Edit2, Check, X, Save, Eye
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

export const AdminFinancesTab = ({ finances, loading, producerMargins, onUpdateMargin, onRefresh }) => {
  const [expandedComm, setExpandedComm] = useState(null);
  const [expandedProducer, setExpandedProducer] = useState(null);
  const [view, setView] = useState('summary');
  const [editingComm, setEditingComm] = useState(null);
  const [editBuyPrice, setEditBuyPrice] = useState('');
  const [editSellPrice, setEditSellPrice] = useState('');
  const [editCommission, setEditCommission] = useState('');
  const [saving, setSaving] = useState(false);

  if (loading || !finances) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="loader" />
      </div>
    );
  }

  const s = finances.summary;

  return (
    <div className="space-y-6">
      {/* Global Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" data-testid="finance-consumer-payments">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <ArrowDownRight className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <p className="text-xs text-green-600 font-medium">Paiements consommateurs</p>
                <p className="text-2xl font-bold text-green-800">{s.total_consumer_payments.toFixed(2)}€</p>
              </div>
            </div>
            <p className="text-xs text-green-600">Ce que les consommateurs paient</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200" data-testid="finance-producer-earnings">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Revenus producteurs</p>
                <p className="text-2xl font-bold text-blue-800">{s.total_producer_earnings.toFixed(2)}€</p>
              </div>
            </div>
            <p className="text-xs text-blue-600">Ce que les producteurs recoivent</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200" data-testid="finance-platform-margin">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Marge brute plateforme</p>
                <p className="text-2xl font-bold text-amber-800">{s.total_platform_margin.toFixed(2)}€</p>
              </div>
            </div>
            <p className="text-xs text-amber-600">Difference achat/vente</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200" data-testid="finance-commissions-collected">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-teal-700" />
              </div>
              <div>
                <p className="text-xs text-teal-600 font-medium">Commissions collectees</p>
                <p className="text-2xl font-bold text-teal-800">{(s.total_commissions_collected || 0).toFixed(2)}€</p>
              </div>
            </div>
            <p className="text-xs text-teal-600">0.01€/kWh sur chaque transaction</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200" data-testid="finance-pro-commissions">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Percent className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <p className="text-xs text-purple-600 font-medium">Commissions pro (50%)</p>
                <p className="text-2xl font-bold text-purple-800">{s.total_pro_commissions.toFixed(2)}€</p>
              </div>
            </div>
            <p className="text-xs text-purple-600">Part reversee aux demarcheurs</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700" data-testid="finance-net-revenue">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-300 font-medium">Revenu net entreprise</p>
                <p className="text-2xl font-bold text-white">{s.platform_net_revenue.toFixed(2)}€</p>
              </div>
            </div>
            <p className="text-xs text-slate-400">Marge - Commissions pro</p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200" data-testid="finance-total-energy">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Energie totale echangee</p>
                <p className="text-2xl font-bold">{s.total_energy_kwh.toFixed(0)} kWh</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{s.total_transactions} transactions</p>
          </CardContent>
        </Card>
      </div>

      {/* Sub-navigation */}
      <div className="flex gap-2 border-b pb-0 overflow-x-auto">
        {[
          { key: 'summary', label: 'Mensuel' },
          { key: 'communities', label: 'Marges communautes' },
          { key: 'producer-margins', label: 'Marges producteurs' },
          { key: 'users', label: 'Par particulier' },
          { key: 'pros', label: 'Commissions pro' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              view === t.key ? 'border-green-600 text-green-700' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`finance-view-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Monthly Summary */}
      {view === 'summary' && (
        <div className="space-y-6">
          {finances.monthly_summary?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" /> Flux financiers mensuels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={finances.monthly_summary}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis unit="€" tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => [`${Number(value).toFixed(2)}€`]} />
                      <Legend />
                      <Bar dataKey="consumer_payments" name="Paiements conso." fill="#22c55e" radius={[4,4,0,0]} />
                      <Bar dataKey="producer_earnings" name="Revenus prod." fill="#3b82f6" radius={[4,4,0,0]} />
                      <Bar dataKey="platform_net" name="Revenu net" fill="#1e293b" radius={[4,4,0,0]} />
                      <Bar dataKey="pro_commissions" name="Comm. pro" fill="#a855f7" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Mois</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Energie</th>
                        <th className="text-right py-2.5 px-3 font-medium text-green-600">Paiements conso.</th>
                        <th className="text-right py-2.5 px-3 font-medium text-blue-600">Revenus prod.</th>
                        <th className="text-right py-2.5 px-3 font-medium text-amber-600">Marge brute</th>
                        <th className="text-right py-2.5 px-3 font-medium text-purple-600">Comm. pro</th>
                        <th className="text-right py-2.5 px-3 font-bold">Revenu net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finances.monthly_summary.map((m, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="py-2.5 px-3 font-medium">{m.month}</td>
                          <td className="py-2.5 px-3 text-right">{m.total_kwh.toFixed(0)} kWh</td>
                          <td className="py-2.5 px-3 text-right text-green-600">{m.consumer_payments.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-blue-600">{m.producer_earnings.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-amber-600">{m.platform_margin.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-purple-600">{m.pro_commissions.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right font-bold">{m.platform_net.toFixed(2)}€</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-bold">
                        <td className="py-2.5 px-3">Total</td>
                        <td className="py-2.5 px-3 text-right">{s.total_energy_kwh.toFixed(0)} kWh</td>
                        <td className="py-2.5 px-3 text-right text-green-600">{s.total_consumer_payments.toFixed(2)}€</td>
                        <td className="py-2.5 px-3 text-right text-blue-600">{s.total_producer_earnings.toFixed(2)}€</td>
                        <td className="py-2.5 px-3 text-right text-amber-600">{s.total_platform_margin.toFixed(2)}€</td>
                        <td className="py-2.5 px-3 text-right text-purple-600">{s.total_pro_commissions.toFixed(2)}€</td>
                        <td className="py-2.5 px-3 text-right">{s.platform_net_revenue.toFixed(2)}€</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* By Community with margin editing */}
      {view === 'communities' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Gestion des marges par communaute
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Cliquez sur une communaute pour voir le detail. Utilisez le bouton modifier pour ajuster les prix d'achat/vente.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Communaute</th>
                      <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Prix achat</th>
                      <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Prix vente</th>
                      <th className="text-right py-2.5 px-3 font-medium text-green-600">Commission</th>
                      <th className="text-right py-2.5 px-3 font-medium text-amber-600">Marge/kWh</th>
                      <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">kWh</th>
                      <th className="text-right py-2.5 px-3 font-medium text-green-600">Commissions</th>
                      <th className="text-right py-2.5 px-3 font-bold">Net entreprise</th>
                      <th className="text-center py-2.5 px-3 font-medium text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finances.community_finances.map((c, i) => (
                      <>
                        <tr
                          key={`row-${i}`}
                          className="border-b hover:bg-muted/30 cursor-pointer"
                          onClick={() => setExpandedComm(expandedComm === c.community_id ? null : c.community_id)}
                          data-testid={`community-finance-${c.community_id}`}
                        >
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2">
                              {expandedComm === c.community_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-xs text-muted-foreground">{c.city}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 text-right">{c.buy_price_per_kwh}€</td>
                          <td className="py-2.5 px-3 text-right">{c.sell_price_per_kwh}€</td>
                          <td className="py-2.5 px-3 text-right text-green-600 font-medium">{(c.commission_per_kwh || 0.01).toFixed(4)}€</td>
                          <td className="py-2.5 px-3 text-right text-amber-600 font-medium">{c.margin_per_kwh}€</td>
                          <td className="py-2.5 px-3 text-right">{c.total_energy_kwh.toFixed(0)}</td>
                          <td className="py-2.5 px-3 text-right text-green-600">{(c.total_commission_collected || 0).toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right font-bold">{c.platform_net_revenue.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-center">
                            <Button
                              size="sm" variant="ghost"
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingComm(c.community_id);
                                setEditBuyPrice(c.buy_price_per_kwh.toString());
                                setEditSellPrice(c.sell_price_per_kwh.toString());
                                setEditCommission((c.commission_per_kwh || 0.01).toString());
                              }}
                              data-testid={`edit-margin-${c.community_id}`}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                        {/* Edit form */}
                        {editingComm === c.community_id && (
                          <tr key={`edit-${i}`}>
                            <td colSpan={9} className="p-0">
                              <div className="bg-blue-50 p-4 border-y border-blue-100">
                                <div className="flex flex-wrap items-end gap-4">
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground block mb-1">Prix achat (€/kWh)</label>
                                    <Input
                                      type="number" step="0.001" min="0.01"
                                      value={editBuyPrice}
                                      onChange={(e) => setEditBuyPrice(e.target.value)}
                                      className="w-32 bg-white"
                                      data-testid="input-buy-price"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground block mb-1">Prix vente (€/kWh)</label>
                                    <Input
                                      type="number" step="0.001" min="0.01"
                                      value={editSellPrice}
                                      onChange={(e) => setEditSellPrice(e.target.value)}
                                      className="w-32 bg-white"
                                      data-testid="input-sell-price"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-green-700 block mb-1">Commission (€/kWh)</label>
                                    <Input
                                      type="number" step="0.001" min="0"
                                      value={editCommission}
                                      onChange={(e) => setEditCommission(e.target.value)}
                                      className="w-32 bg-white border-green-200"
                                      data-testid="input-commission"
                                    />
                                  </div>
                                  <div className="text-sm">
                                    <span className="text-xs text-muted-foreground block mb-1">Marge prevue</span>
                                    <span className="font-bold text-amber-600">
                                      {(parseFloat(editBuyPrice || 0) - parseFloat(editSellPrice || 0)).toFixed(4)}€/kWh
                                    </span>
                                  </div>
                                  <div className="text-sm">
                                    <span className="text-xs text-green-700 block mb-1">Cout conso.</span>
                                    <span className="font-bold text-green-700">
                                      {(parseFloat(editBuyPrice || 0) + parseFloat(editCommission || 0)).toFixed(4)}€/kWh
                                    </span>
                                  </div>
                                  <div className="flex gap-2 ml-auto">
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                      disabled={saving}
                                      onClick={async () => {
                                        setSaving(true);
                                        await onUpdateMargin(c.community_id, parseFloat(editBuyPrice), parseFloat(editSellPrice), parseFloat(editCommission));
                                        setEditingComm(null);
                                        setSaving(false);
                                      }}
                                      data-testid="save-margin"
                                    >
                                      <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingComm(null)}>
                                      <X className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                                <p className="text-xs text-blue-600 mt-2">
                                  Un email sera envoye automatiquement aux consommateurs si le prix d'achat change, et aux producteurs si le prix de vente change.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* Expanded detail */}
                        {expandedComm === c.community_id && editingComm !== c.community_id && (
                          <tr key={`exp-${i}`}>
                            <td colSpan={8} className="p-0">
                              <div className="bg-slate-50 p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                                <div className="bg-white p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Membres</p>
                                  <p className="font-bold">{c.member_count}</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Revenus conso.</p>
                                  <p className="font-bold text-green-600">{c.total_buy_revenue.toFixed(2)}€</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Verse aux prod.</p>
                                  <p className="font-bold text-blue-600">{c.total_sell_cost.toFixed(2)}€</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Comm. pro</p>
                                  <p className="font-bold text-purple-600">{c.pro_commissions.toFixed(2)}€</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Transactions</p>
                                  <p className="font-bold">{c.tx_count}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-bold">
                      <td className="py-2.5 px-3">Total ({finances.community_finances.length})</td>
                      <td className="py-2.5 px-3 text-right" colSpan={3}></td>
                      <td className="py-2.5 px-3 text-right">{s.total_energy_kwh.toFixed(0)}</td>
                      <td className="py-2.5 px-3 text-right text-amber-600">{s.total_platform_margin.toFixed(2)}€</td>
                      <td className="py-2.5 px-3 text-right">{s.platform_net_revenue.toFixed(2)}€</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Producer Margins Detail */}
      {view === 'producer-margins' && (
        <div className="space-y-4">
          {producerMargins && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold">{producerMargins.totals.total_producers}</p>
                    <p className="text-xs text-muted-foreground">Producteurs actifs</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-amber-600">{producerMargins.totals.total_margin.toFixed(2)}€</p>
                    <p className="text-xs text-muted-foreground">Marge totale generee</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-purple-600">{producerMargins.totals.total_pro_commission.toFixed(2)}€</p>
                    <p className="text-xs text-muted-foreground">Part parrains (50%)</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-slate-800 to-slate-900">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-white">{producerMargins.totals.total_platform_net.toFixed(2)}€</p>
                    <p className="text-xs text-slate-400">Net entreprise</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" /> Marges generees par chaque producteur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Producteur</th>
                          <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">kWh vendu</th>
                          <th className="text-right py-2.5 px-3 font-medium text-green-600">Conso. paye</th>
                          <th className="text-right py-2.5 px-3 font-medium text-blue-600">Prod. recoit</th>
                          <th className="text-right py-2.5 px-3 font-medium text-amber-600">Marge</th>
                          <th className="text-right py-2.5 px-3 font-medium text-purple-600">Comm. pro</th>
                          <th className="text-right py-2.5 px-3 font-bold">Net entreprise</th>
                          <th className="text-center py-2.5 px-3 font-medium text-muted-foreground">Parrain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {producerMargins.producers.map((p, i) => (
                          <>
                            <tr
                              key={`p-${i}`}
                              className="border-b hover:bg-muted/30 cursor-pointer"
                              onClick={() => setExpandedProducer(expandedProducer === p.id ? null : p.id)}
                              data-testid={`producer-margin-${p.id}`}
                            >
                              <td className="py-2.5 px-3">
                                <div className="flex items-center gap-2">
                                  {expandedProducer === p.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  <div>
                                    <p className="font-medium">{p.name}</p>
                                    <p className="text-xs text-muted-foreground">{p.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-2.5 px-3 text-right">{p.total_kwh.toFixed(0)}</td>
                              <td className="py-2.5 px-3 text-right text-green-600">{p.total_consumer_paid.toFixed(2)}€</td>
                              <td className="py-2.5 px-3 text-right text-blue-600">{p.total_producer_earned.toFixed(2)}€</td>
                              <td className="py-2.5 px-3 text-right text-amber-600 font-medium">{p.total_margin.toFixed(2)}€</td>
                              <td className="py-2.5 px-3 text-right text-purple-600">
                                {p.pro_commission > 0 ? `${p.pro_commission.toFixed(2)}€` : '-'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-bold">{p.platform_net.toFixed(2)}€</td>
                              <td className="py-2.5 px-3 text-center">
                                {p.has_sponsor ? (
                                  <Badge className="bg-purple-100 text-purple-700 text-[10px]">{p.sponsor_name}</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-500 text-[10px]">Aucun</Badge>
                                )}
                              </td>
                            </tr>
                            {expandedProducer === p.id && p.community_breakdown && (
                              <tr key={`pe-${i}`}>
                                <td colSpan={8} className="p-0">
                                  <div className="bg-slate-50 p-4">
                                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase">Detail par communaute</p>
                                    <div className="grid gap-2">
                                      {p.community_breakdown.map((cb, j) => (
                                        <div key={j} className="bg-white rounded-lg p-3 flex items-center justify-between">
                                          <div>
                                            <p className="font-medium text-sm">{cb.community_name}</p>
                                            <p className="text-xs text-muted-foreground">{cb.tx_count} tx | Achat: {cb.buy_price}€ / Vente: {cb.sell_price}€</p>
                                          </div>
                                          <div className="flex gap-4 text-right text-sm">
                                            <div>
                                              <p className="font-medium">{cb.kwh.toFixed(0)} kWh</p>
                                              <p className="text-xs text-muted-foreground">produit</p>
                                            </div>
                                            <div>
                                              <p className="font-medium text-green-600">{cb.consumer_paid.toFixed(2)}€</p>
                                              <p className="text-xs text-green-600">paye</p>
                                            </div>
                                            <div>
                                              <p className="font-medium text-blue-600">{cb.producer_earned.toFixed(2)}€</p>
                                              <p className="text-xs text-blue-600">recoit</p>
                                            </div>
                                            <div>
                                              <p className="font-bold text-amber-600">{cb.margin.toFixed(2)}€</p>
                                              <p className="text-xs text-amber-600">marge</p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-bold">
                          <td className="py-2.5 px-3">Total ({producerMargins.totals.total_producers})</td>
                          <td className="py-2.5 px-3 text-right">{producerMargins.totals.total_kwh.toFixed(0)}</td>
                          <td className="py-2.5 px-3 text-right text-green-600">{producerMargins.totals.total_consumer_paid.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-blue-600">{producerMargins.totals.total_producer_earned.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-amber-600">{producerMargins.totals.total_margin.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-purple-600">{producerMargins.totals.total_pro_commission.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right">{producerMargins.totals.total_platform_net.toFixed(2)}€</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          {!producerMargins && (
            <div className="flex items-center justify-center py-20 text-muted-foreground">Chargement...</div>
          )}
        </div>
      )}

      {/* By User */}
      {view === 'users' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Producers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                <ArrowUpRight className="w-5 h-5" /> Top producteurs (revenus)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Producteur</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">kWh</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">Gagne</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground">Parraine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finances.top_producers.map((p, i) => (
                      <tr key={i} className="border-b hover:bg-muted/30" data-testid={`top-producer-${i}`}>
                        <td className="py-2 px-2">
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[160px]">{p.email}</p>
                        </td>
                        <td className="py-2 px-2 text-right">{p.total_kwh.toFixed(0)}</td>
                        <td className="py-2 px-2 text-right font-medium text-green-600">{p.net_earned.toFixed(2)}€</td>
                        <td className="py-2 px-2 text-center">
                          {p.has_sponsor ? (
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]">Oui</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 text-[10px]">Non</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Top Consumers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <ArrowDownRight className="w-5 h-5" /> Top consommateurs (depenses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Consommateur</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">kWh</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">Depense</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finances.top_consumers.map((c, i) => (
                      <tr key={i} className="border-b hover:bg-muted/30" data-testid={`top-consumer-${i}`}>
                        <td className="py-2 px-2">
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[160px]">{c.email}</p>
                        </td>
                        <td className="py-2 px-2 text-right">{c.total_kwh.toFixed(0)}</td>
                        <td className="py-2 px-2 text-right font-medium text-blue-600">{c.total_spent.toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pro Commissions Management */}
      {view === 'pros' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Percent className="w-5 h-5 text-purple-600" /> Gestion des commissions professionnelles
              </CardTitle>
            </CardHeader>
            <CardContent>
              {finances.pro_commissions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun professionnel enregistre</p>
              ) : (
                <div className="space-y-4">
                  {finances.pro_commissions.map((pc, i) => (
                    <div key={i} className="border rounded-xl p-4 hover:border-purple-200 transition-colors" data-testid={`pro-commission-card-${pc.pro_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center">
                            <span className="text-lg font-bold text-purple-700">{pc.pro_name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-semibold">{pc.pro_name}</p>
                            <p className="text-sm text-muted-foreground">{pc.pro_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-purple-700">{pc.commission_earned.toFixed(2)}€</p>
                          <p className="text-xs text-muted-foreground">commission totale</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{pc.referral_count}</p>
                          <p className="text-[10px] text-muted-foreground">Filleuls total</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-green-700">{pc.producer_referrals || 0}</p>
                          <p className="text-[10px] text-green-600">Producteurs</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-blue-700">{pc.total_energy_kwh.toFixed(0)}</p>
                          <p className="text-[10px] text-blue-600">kWh echanges</p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-amber-700">{pc.total_margin.toFixed(2)}€</p>
                          <p className="text-[10px] text-amber-600">Marge generee</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-purple-700">{pc.commission_pending.toFixed(2)}€</p>
                          <p className="text-[10px] text-purple-600">A verser</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recap */}
          <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Euro className="w-5 h-5" /> Repartition des revenus
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold">{s.total_producer_earnings.toFixed(2)}€</p>
                  <p className="text-xs text-slate-300">Verse aux producteurs</p>
                  <p className="text-xs text-slate-400 mt-1">{((s.total_producer_earnings / s.total_consumer_payments) * 100).toFixed(1)}% du total</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-300">{s.total_pro_commissions.toFixed(2)}€</p>
                  <p className="text-xs text-slate-300">Commissions pro</p>
                  <p className="text-xs text-slate-400 mt-1">{((s.total_pro_commissions / s.total_consumer_payments) * 100).toFixed(1)}% du total</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-300">{s.platform_net_revenue.toFixed(2)}€</p>
                  <p className="text-xs text-slate-300">Revenu net entreprise</p>
                  <p className="text-xs text-slate-400 mt-1">{((s.platform_net_revenue / s.total_consumer_payments) * 100).toFixed(1)}% du total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
